var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Routes, Route } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import { useCustomizationActions } from './common/services/customizations-service/query';
import { useShowBalanceActions } from './common/services/show-balance-service/query';
import { useCustomizationStore } from './common/services/customizations-service/service-store/service-store';
import { useCompanyAdditionalInfoStore } from './common/services/company-additional-info-service/service-store/service-store';
import { useProfileStore } from 'common/services/profile-service/service-store/service-store';
import { useComplianceStore } from './common/services/compliance-service/service-store/service-store';
import { useSupportRequestTypesStore } from './common/services/support-request-types-service/service-store/service-store';
import { useConversionStore } from './common/services/conversion-service/service-store/service-store';
import { complianceServiceInitializedSelector } from './common/services/compliance-service/service-store/selectors';
import { customizationsServiceInitializedSelector } from 'common/services/customizations-service/service-store/selectors';
import { profileServiceInitializedSelector } from 'common/services/profile-service/service-store/selectors';
import { GuestLayout, MainLayout, RegistrationLayout, UserSelfRegistrationLayout, OpenPagesLayout, ComplianceLayout, } from 'common/layouts';
import { RequiredAuth } from 'common/components/required-auth';
import { AuthorizedAppGuard } from 'common/components/authorized-app-guard';
import { EventsListener } from 'common/components/events-listener';
import { ScrollUp } from 'common/components/scroll-up';
import { HashUrlListener } from 'common/components/hash-url-listener';
import { Toast } from 'ui-kit/toast';
import { ModalsContainer } from './ui-kit/modals';
import { FallbackComponent } from 'common/components/fallback-component';
import { CookiesAgreement } from 'common/components/cookies-agreement';
import { ThemeObserver } from 'common/components/theme-observer/theme-observer';
import { InitializationAdditionalScripts } from 'common/components/initialization-additional-scripts';
import { Wallpaper } from 'common/components/wallpaper';
import { BaseServiceObserver } from './common/components/base-service-observer';
import { OnlyCreateCompany } from './common/components/only-create-company';
import { NoMatch } from 'pages/error-pages/no-match';
import { RoleType } from 'common/types/server-api-dtos';
import { userService } from 'common/utils/user-service';
import { getPartRoutes } from 'common/helpers/get-part-routes';
import i18next from './i18n-service/i18n';
export var Initialization = function (_a) {
    var children = _a.children;
    var resetProfileStateAction = useProfileStore(function (state) { return state.resetState; });
    var resetCompanyAdditionalInfoStateAction = useCompanyAdditionalInfoStore(function (state) { return state.resetState; });
    var resetConversionState = useConversionStore(function (state) { return state.resetState; });
    var resetComplianceState = useComplianceStore(function (state) { return state.resetState; });
    var resetSupportRequestTypesStateAction = useSupportRequestTypesStore(function (state) { return state.resetState; });
    var resetShowThanksBalanceStateAction = useShowBalanceActions().resetShowBalanceStateAction;
    var resetCustomizationsAction = useCustomizationActions().resetCustomizationsAction;
    var isAuth = userService.isLoggedIn();
    var isCompanyCreator = userService.hasRole(RoleType.CompanyCreator);
    var isProfileInitialized = useProfileStore(profileServiceInitializedSelector);
    var isCustomizationsInialized = useCustomizationStore(customizationsServiceInitializedSelector);
    var isComplianceInitialized = useComplianceStore(complianceServiceInitializedSelector);
    var showWallpaper = useMemo(function () {
        return isAuth && !isCompanyCreator && (!isProfileInitialized || !isCustomizationsInialized || !isComplianceInitialized);
    }, [isAuth, isCompanyCreator, isProfileInitialized, isCustomizationsInialized, isComplianceInitialized]);
    useEffect(function () {
        var currentLanguage = i18next.language;
        document.documentElement.setAttribute('lang', currentLanguage);
        document.documentElement.setAttribute('dir', currentLanguage.includes('ar') ? 'rtl' : 'ltr');
    }, []);
    useEffect(function () {
        if (!isAuth) {
            var handler_1 = function () {
                resetCustomizationsAction();
            };
            window.addEventListener('unload', handler_1);
            return function () {
                resetConversionState();
                resetComplianceState();
                resetProfileStateAction();
                resetShowThanksBalanceStateAction();
                resetSupportRequestTypesStateAction();
                resetCompanyAdditionalInfoStateAction();
                window.removeEventListener('unload', handler_1);
            };
        }
    }, [
        resetComplianceState,
        resetShowThanksBalanceStateAction,
        resetConversionState,
        resetProfileStateAction,
        resetCustomizationsAction,
        resetSupportRequestTypesStateAction,
        resetCompanyAdditionalInfoStateAction,
        isAuth,
    ]);
    return showWallpaper ? _jsx(Wallpaper, {}) : _jsx(_Fragment, { children: children });
};
var AppContent = function () {
    var routes = useMemo(function () { return ({
        userRoutes: getPartRoutes('userRoutes'),
        basic: getPartRoutes('basic'),
        registration: getPartRoutes('registration'),
        userSelfRegistration: getPartRoutes('userSelfRegistration'),
        guestRoutes: getPartRoutes('guest'),
        openPagesRoutes: getPartRoutes('openPages'),
        complianceRoutes: getPartRoutes('compliance'),
    }); }, []);
    return (_jsxs(Routes, { children: [_jsxs(Route, __assign({ element: _jsx(RequiredAuth, { children: _jsx(OnlyCreateCompany, { withOutlet: true }) }) }, { children: [_jsx(Route, __assign({ path: "/", element: _jsx(RegistrationLayout, {}) }, { children: routes.registration.adaptivePages })), _jsx(Route, __assign({ path: "/", element: _jsx(UserSelfRegistrationLayout, {}) }, { children: routes.userSelfRegistration.adaptivePages }))] })), _jsxs(Route, __assign({ element: _jsx(AuthorizedAppGuard, {}) }, { children: [_jsxs(Route, __assign({ path: "/", element: _jsx(MainLayout, {}) }, { children: [routes.userRoutes.adaptivePages, routes.basic.adaptivePages] })), _jsxs(Route, __assign({ path: "/", element: _jsx(MainLayout, { withRiseze: true }) }, { children: [routes.userRoutes.notAdaptivePages, routes.basic.notAdaptivePages] })), _jsx(Route, __assign({ path: "/", element: _jsx(ComplianceLayout, {}) }, { children: routes.complianceRoutes.adaptivePages })), _jsx(Route, __assign({ path: "/", element: _jsx(OpenPagesLayout, {}) }, { children: routes.openPagesRoutes.adaptivePages }))] })), _jsx(Route, __assign({ path: "/", element: _jsx(GuestLayout, {}) }, { children: routes.guestRoutes.adaptivePages })), _jsx(Route, __assign({ path: "/", element: _jsx(GuestLayout, { withRiseze: true }) }, { children: routes.guestRoutes.notAdaptivePages })), _jsx(Route, { path: "*", element: _jsx(NoMatch, {}) })] }));
};
var App = function () { return (_jsxs("div", __assign({ style: { position: 'relative' } }, { children: [_jsx(Initialization, { children: _jsxs(ErrorBoundary, __assign({ FallbackComponent: FallbackComponent }, { children: [_jsx(AppContent, {}), _jsx(ScrollUp, {}), _jsx(HashUrlListener, {}), _jsx(InitializationAdditionalScripts, {})] })) }), _jsx(Toast, {}), _jsx(ModalsContainer, {}), _jsx(BaseServiceObserver, {}), _jsx(ThemeObserver, {}), _jsx(EventsListener, {}), _jsx(CookiesAgreement, {})] }))); };
export default withTranslation()(App);
